// src/services/websocketSingleton.js
// ✅ WEBSOCKET SINGLETON GLOBAL - Usado por todos os hooks

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3007';
// ⏱️ Hard-stops / timeouts
const MAX_CONNECT_TIME_MS = 8000;         // máximo esperando onopen por tentativa
const MAX_RECONNECT_ATTEMPTS = 12;        // máximo de reconexões seguidas
const MAX_RETRY_WINDOW_MS = 5 * 60_000;   // janela total de tentativas (5 min)
const PLAYER_COMMANDS = new Set([
  'PLAYER_PREV',
  'PLAYER_NEXT',
  'PLAYER_ADD_MUSIC',
  'PLAYER_PAUSE',
  'PLAYER_RESUME',
  'PLAYER_LIST_TRACKS',
  'PLAYER_REMOVE_TRACK'
]);

// ============================================
// SINGLETON GLOBAL
// ============================================
let globalWS = null;
let wsListeners = new Set();
let reconnectTimeout = null;
let connectTimer = null;    
let connectionAttempts = 0;
let isIntentionallyClosed = false;
let idleCloseTimer = null;
let firstFailureAt = null; // ✅ declarar a variável usada no controle de janela
let registeredClienteId = null;
let registrationError = null;

/**
 * Capturar informações de origem para tracking
 */
function captureOriginInfo() {
  try {
    const error = new Error();
    const stack = error.stack;
    const stackLines = stack.split('\n');
    
    // Pegar linha que chamou (geralmente a 3ª ou 4ª)
    const callerLine = stackLines[3] || stackLines[2] || 'Unknown';
    
    // Parse: "at Component (http://localhost:3000/static/js/bundle.js:1234:56)"
    const match = callerLine.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
    const componentName = match ? match[1] : 'Unknown';
    const filePath = match ? match[2] : 'Unknown';
    const lineNumber = match ? match[3] : '?';
    
    return {
      componentName,
      createdFrom: `${filePath}:${lineNumber}`,
      stackTrace: stack.substring(0, 500),
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    return {
      componentName: 'Unknown',
      createdFrom: 'Unknown',
      stackTrace: '',
      timestamp: new Date().toISOString()
    };
  }
}

function getSavedAuth() {
  try {
    return {
      user: JSON.parse(localStorage.getItem('radio-ai-user') || '{}'),
    };
  } catch {
    return { user: {} };
  }
}

function notifyListeners(event) {
  const listenerCount = wsListeners.size;
  let successCount = 0;
  let errorCount = 0;

  wsListeners.forEach(listener => {
    try {
      listener(event);
      successCount++;
    } catch (error) {
      console.error('[WS Singleton] ❌ Erro em listener:', error);
      errorCount++;
    }
  });

  if (listenerCount > 1) {
    const status = `${successCount} OK${errorCount > 0 ? `, ${errorCount} ERR` : ''}`;
    console.log(`[WS Singleton] 📢 Distribuído para ${listenerCount} listeners: ${status}`);
  }
}

function emitLocalMessage(message) {
  notifyListeners({ data: JSON.stringify(message), synthetic: true });
}

/**
 * Criar ou obter WebSocket existente
 */
function getOrCreateWebSocket() {
  // ✅ Reutilizar conexão existente
  if (globalWS && (
    globalWS.readyState === WebSocket.OPEN || 
    globalWS.readyState === WebSocket.CONNECTING
  )) {
    console.log('[WS Singleton] ♻️ Reutilizando conexão existente');
    console.log(`[WS Singleton] 👥 Listeners ativos: ${wsListeners.size}`);
    return globalWS;
  }
  
  // ✅ Criar nova conexão
  connectionAttempts++;
  console.log('[WS Singleton] 🆕 Criando nova conexão WebSocket');
  console.log(`[WS Singleton] 🔢 Tentativa: ${connectionAttempts}`);
  
  const ws = new WebSocket(WS_URL);
  globalWS = ws;
  registeredClienteId = null;
  registrationError = null;
  isIntentionallyClosed = false;
  if (!firstFailureAt) firstFailureAt = Date.now();

  // ⏱️ Timeout de conexão por tentativa
  clearTimeout(connectTimer);
  connectTimer = setTimeout(() => {
    if (globalWS && globalWS.readyState === WebSocket.CONNECTING) {
      console.warn('[WS Singleton] ⏱️ Timeout de conexão; abortando tentativa');
      try { isIntentionallyClosed = false; globalWS.close(4000, 'Connect timeout'); } catch {}
    }
  }, MAX_CONNECT_TIME_MS);
  
  const originInfo = captureOriginInfo();
  
  ws.onopen = () => {
    clearTimeout(connectTimer);
    console.log('╔════════════════════════════════════════╗');
    console.log('║  ✅ WEBSOCKET SINGLETON CONECTADO      ║');
    console.log('╠════════════════════════════════════════╣');
    console.log(`║  👥 Listeners: ${wsListeners.size.toString().padEnd(24)} ║`);
    console.log(`║  🔢 Tentativas: ${connectionAttempts.toString().padEnd(22)} ║`);
    console.log('╚════════════════════════════════════════╝');
    
    connectionAttempts = 0; // Reset após sucesso
    firstFailureAt = null;  // encerra a janela de falhas
    emitLocalMessage({ type: 'WS_OPEN', timestamp: new Date().toISOString() });
    
    // ✅ Enviar informações de origem para o servidor
    try {
      ws.send(JSON.stringify({
        type: 'CLIENT_ORIGIN',
        origin: originInfo
      }));
      console.log('[WS Singleton] 📤 Informações de origem enviadas');
    } catch (error) {
      console.error('[WS Singleton] ❌ Erro ao enviar origem:', error);
    }

    // ✅ Registrar browser com clienteId para receber MUSIC_STATUS e enviar comandos ao exe
    try {
      const { user: savedUser } = getSavedAuth();
      if (savedUser.id) {
        ws.send(JSON.stringify({
          type: 'REGISTER',
          clienteId: savedUser.id,
          role: 'browser'
        }));
        console.log('[WS Singleton] 📤 REGISTER enviado — clienteId:', savedUser.id);
      } else {
        registrationError = 'AUTH_MISSING_LOCAL';
        emitLocalMessage({
          type: 'REGISTER_REJECTED',
          reason: registrationError,
          timestamp: new Date().toISOString()
        });
        console.warn('[WS Singleton] REGISTER não enviado — usuário local ausente');
      }
    } catch (error) {
      console.error('[WS Singleton] ❌ Erro ao enviar REGISTER:', error);
    }
  };
  
  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'REGISTER_OK') {
        registeredClienteId = msg.clienteId || null;
        registrationError = null;
      }
      if (msg.type === 'REGISTER_REJECTED') {
        registeredClienteId = null;
        registrationError = msg.reason || 'REGISTER_REJECTED';
        console.warn('[WS Singleton] REGISTER rejeitado:', msg.reason || 'sem motivo');
      }
    } catch {}

    // ✅ Distribuir mensagem para TODOS os listeners
    notifyListeners(event);
  };
  
  ws.onerror = (error) => {
    emitLocalMessage({ type: 'WS_ERROR', timestamp: new Date().toISOString() });
    console.error('[WS Singleton] ❌ Erro de conexão:', error);
  };
  
  ws.onclose = (event) => {
    clearTimeout(connectTimer);
    console.log('[WS Singleton] 🔌 Conexão fechada');
    console.log(`   Código: ${event.code}, Razão: ${event.reason || 'N/A'}`);
    
    globalWS = null;
    registeredClienteId = null;
    registrationError = null;
    emitLocalMessage({
      type: 'WS_CLOSED',
      code: event.code,
      reason: event.reason || '',
      timestamp: new Date().toISOString()
    });
    
    // 🔄 Reconecta só se não foi fechamento intencional e ainda há listeners
    const shouldRetry = !isIntentionallyClosed && wsListeners.size > 0;
    
    // Reset flag
    isIntentionallyClosed = false;
    
    if (shouldRetry) {
      const attemptsExceeded = connectionAttempts >= MAX_RECONNECT_ATTEMPTS;
      const windowExceeded = firstFailureAt && (Date.now() - firstFailureAt >= MAX_RETRY_WINDOW_MS);
      if (attemptsExceeded || windowExceeded) {
        console.error('[WS Singleton] 🧯 Limite de reconexões atingido; parando tentativas.');
        // (Opcional) notificar a UI com um evento sintético
        emitLocalMessage({ type: 'WS_FATAL', reason: attemptsExceeded ? 'max_attempts' : 'time_window' });
        return; // ❗ não agenda nova reconexão
      }
      // Backoff exponencial: 1s, 2s, 4s, 8s, máximo 30s
      const delay = Math.min(1000 * Math.pow(2, Math.min(connectionAttempts, 5)), 30000);
      
      console.log(`[WS Singleton] 🔄 Reconectando em ${(delay / 1000).toFixed(1)}s...`);
      console.log(`[WS Singleton] 👥 Listeners aguardando: ${wsListeners.size}`);
      
      reconnectTimeout = setTimeout(() => {
        // Double-check antes de reconectar
        if (wsListeners.size > 0 && !isIntentionallyClosed) {
          getOrCreateWebSocket();
        } else {
          console.log('[WS Singleton] ⏹️ Reconexão cancelada (sem listeners)');
        }
      }, delay);
    } else {
      console.log('[WS Singleton] 🛑 Não reconectará (sem listeners ou fechamento intencional)');
    }
  };
  
  return ws;
}

/**
 * Adicionar listener ao WebSocket
 * @param {Function} listener - Função que recebe (event)
 * @param {string} [name] - Nome opcional para debug
 */
export function addWebSocketListener(listener, name = 'Unknown') {
  if (!listener || typeof listener !== 'function') {
    console.error('[WS Singleton] ❌ Listener inválido');
    return;
  }
  
  if (wsListeners.has(listener)) {
    console.warn(`[WS Singleton] ⚠️ Listener "${name}" já estava registrado`);
    return;
  }
  
  wsListeners.add(listener);
  console.log(`[WS Singleton] ➕ Listener "${name}" adicionado. Total: ${wsListeners.size}`);

  // Cancelar timer de fechamento se houver
  if (idleCloseTimer) {
    clearTimeout(idleCloseTimer);
    idleCloseTimer = null;
    console.log('[WS Singleton] ⏱️ Timer de fechamento cancelado');
  }
  
  // ✅ Criar conexão se não existir
  getOrCreateWebSocket();
}

/**
 * Remover listener do WebSocket
 * @param {Function} listener - Função registrada
 * @param {string} [name] - Nome opcional para debug
 */
export function removeWebSocketListener(listener, name = 'Unknown') {
  if (!listener) {
    console.error('[WS Singleton] ❌ Listener inválido para remoção');
    return;
  }
  
  const wasPresent = wsListeners.delete(listener);
  
  if (wasPresent) {
    console.log(`[WS Singleton] ➖ Listener "${name}" removido. Total: ${wsListeners.size}`);
  } else {
    console.warn(`[WS Singleton] ⚠️ Listener "${name}" não estava registrado`);
  }
  
  // ⏳ Fechar conexão após timeout se não houver mais listeners
  if (wsListeners.size === 0 && globalWS) {
    console.log('[WS Singleton] ⏳ Agendando fechamento em 1500ms (StrictMode safety)');
    
    // Limpar timers anteriores
    clearTimeout(reconnectTimeout);
    clearTimeout(idleCloseTimer);
    
    // Agendar fechamento
    idleCloseTimer = setTimeout(() => {
      // Double-check: realmente sem listeners?
      if (wsListeners.size === 0 && globalWS) {
        console.log('[WS Singleton] 🛑 Fechando conexão (sem listeners)');
        isIntentionallyClosed = true;
        
        try {
          globalWS.close(1000, 'No listeners');
        } catch (error) {
          console.error('[WS Singleton] ❌ Erro ao fechar:', error);
        } finally {
          globalWS = null;
        }
      } else {
        console.log('[WS Singleton] ↩️ Fechamento cancelado (novos listeners)');
      }
    }, 1500);
  }
}

/**
 * Enviar mensagem com segurança
 * @param {string} type - Tipo da mensagem
 * @param {Object} data - Dados adicionais
 * @returns {boolean} - Sucesso do envio
 */
export function sendWebSocketMessage(type, data = {}) {
  if (!globalWS) {
    console.warn('[WS Singleton] ⚠️ WebSocket não existe');
    return false;
  }

  if (globalWS.readyState !== WebSocket.OPEN) {
    console.warn(`[WS Singleton] ⚠️ WebSocket não está OPEN (estado: ${globalWS.readyState})`);
    return false;
  }

  try {
    const message = {
      type,
      ...data,
      timestamp: new Date().toISOString()
    };

    if (PLAYER_COMMANDS.has(type) && !registeredClienteId) {
      console.warn(`[WS Singleton] ⚠️ Comando ${type} bloqueado — browser ainda não autenticado no WS`);
      return false;
    }
    
    globalWS.send(JSON.stringify(message));
    console.log(`[WS Singleton] 📤 Mensagem enviada: ${type}`);
    return true;
  } catch (error) {
    console.error('[WS Singleton] ❌ Erro ao enviar mensagem:', error);
    return false;
  }
}

/**
 * Obter status da conexão
 * @returns {Object} Status detalhado
 */
export function getWebSocketStatus() {
  const readyStateNames = {
    [WebSocket.CONNECTING]: 'CONNECTING',
    [WebSocket.OPEN]: 'OPEN',
    [WebSocket.CLOSING]: 'CLOSING',
    [WebSocket.CLOSED]: 'CLOSED'
  };
  
  return {
    connected: globalWS?.readyState === WebSocket.OPEN,
    connecting: globalWS?.readyState === WebSocket.CONNECTING,
    readyState: globalWS?.readyState,
    readyStateName: globalWS ? readyStateNames[globalWS.readyState] : 'NULL',
    listeners: wsListeners.size,
    connectionAttempts,
    url: WS_URL,
    authenticated: !!registeredClienteId,
    registeredClienteId,
    registrationError,
    hasIdleTimer: !!idleCloseTimer,
    hasReconnectTimer: !!reconnectTimeout
  };
}

/**
 * Fechar conexão manualmente (para debug/cleanup)
 */
export function closeWebSocket() {
  console.log('[WS Singleton] 🛑 Fechamento manual solicitado');
  
  isIntentionallyClosed = true;
  wsListeners.clear();
  
  // Limpar todos os timers
  if (reconnectTimeout) { clearTimeout(reconnectTimeout); reconnectTimeout = null; }
  if (idleCloseTimer)   { clearTimeout(idleCloseTimer);   idleCloseTimer   = null; }
  if (connectTimer)     { clearTimeout(connectTimer);     connectTimer     = null; }
  firstFailureAt = null; // ✅ sempre resetar, agora que existe
  registeredClienteId = null;
  registrationError = null;
  
  // Fechar WebSocket
  if (globalWS) {
    try {
      globalWS.close(1000, 'Manual close');
    } catch (error) {
      console.error('[WS Singleton] ❌ Erro ao fechar:', error);
    } finally {
      globalWS = null;
    }
  }
  
  console.log('[WS Singleton] ✅ Fechamento completo');
}

/**
 * Forçar reconexão (útil para debug)
 */
export function forceReconnect() {
  console.log('[WS Singleton] 🔄 Reconexão forçada');
  
  if (globalWS) {
    globalWS.close();
  }
  
  connectionAttempts = 0;
  
  if (wsListeners.size > 0) {
    getOrCreateWebSocket();
  } else {
    console.warn('[WS Singleton] ⚠️ Sem listeners para reconectar');
  }
}

// ============================================
// DEBUG - Expor no window em desenvolvimento
// ============================================
if (process.env.NODE_ENV === 'development') {
  window.__WebSocketSingleton__ = {
    getStatus: getWebSocketStatus,
    close: closeWebSocket,
    reconnect: forceReconnect,
    getListenerCount: () => wsListeners.size,
    getListeners: () => Array.from(wsListeners).map((l, i) => `Listener ${i + 1}`),
    clearListeners: () => {
      wsListeners.clear();
      console.log('[WS Singleton] 🧹 Todos os listeners removidos');
    }
  };
  
  console.log('[WS Singleton] 🛠️ Debug disponível em window.__WebSocketSingleton__');
  console.log('Comandos:');
  console.log('  window.__WebSocketSingleton__.getStatus()');
  console.log('  window.__WebSocketSingleton__.reconnect()');
  console.log('  window.__WebSocketSingleton__.close()');
}

// ============================================
// EXPORTS
// ============================================
export default {
  addListener: addWebSocketListener,
  removeListener: removeWebSocketListener,
  send: sendWebSocketMessage,
  getStatus: getWebSocketStatus,
  close: closeWebSocket,
  reconnect: forceReconnect
};
